import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import FormikFormLine from "../../../formik-form-components/FormikFormLine";
import FormikSelect from "../../../formik-form-components/FormikSelect";
import * as Yup from "yup";
import { useBoardFromContext } from "../../../../utils/hooks/commonly-stored/uploaded-board/board";
import { Board } from "../../../../reducers/uploadedBoard";
import {
  FileUploadContainer,
  MessagesFieldset,
} from "./CustomerMessagesStyles";
import Button from "../../../common/Button";
import { ButtonTypes } from "../../../common/Button/constants";
import { useAppSelector } from "../../../../utils/hooks/store";
import LoadingOverlay from "../../../LoadingOverlay";
import FormikCheckbox from "../../../formik-form-components/FormikCheckbox";
import FileInput from "../../../common/FileInput";
import ModalContentWithCloseButton from "../../../common/ModalContentWithCloseButton";
import Modal from "../../../common/Modal";
import { FakeLink } from "../../../../constants/Styles";
import {
  CustomerMessageFormValues,
  useCustomerMessage,
  useGetBoardMessages,
} from "../../../../utils/hooks/commonly-stored/messages";
import { QuoteToolContainer, QuoteToolTitle } from "../QuoteStyles";
import { useBillOfMaterials } from "../../../../utils/hooks/commonly-stored/uploaded-board/updates/billOfMaterials";
import MessageList from "./MessageList";

const customerMessageSchema = Yup.object().shape({
  designators: Yup.array().of(Yup.string()).nullable(),
  message: Yup.string().required("Please enter your message"),
  internalOnly: Yup.boolean(),
});

function boardMessageKey(boardId: string): string {
  return `FPCB-message-${boardId}`;
}

function boardMessageInternalKey(boardId: string): string {
  return `FPCB-message-internal-${boardId}`;
}

function StoreMessage({
  boardId,
  message,
  internalOnly,
}: {
  boardId: string;
  message: string;
  internalOnly: boolean;
}) {
  useEffect(() => {
    const key = boardMessageKey(boardId);
    const internalKey = boardMessageInternalKey(boardId);
    if (message.trim()) {
      localStorage.setItem(key, message);
      localStorage.setItem(internalKey, internalOnly ? "true" : "false");
    } else {
      localStorage.removeItem(key);
      localStorage.removeItem(internalKey);
    }
  }, [boardId, message, internalOnly]);

  return null;
}

const CustomerMessages = () => {
  const boardStuff = useBoardFromContext();
  const board = boardStuff.board as Board;
  const billOfMaterials = useBillOfMaterials();
  const isAdminOrEngineer = useAppSelector(
    (state) => state.auth.isAdminOrEngineer,
  );
  const [files, setFiles] = useState<File[]>([]);
  const [uploadFiles, setUploadFiles] = useState<boolean>(false);
  const { messagesResponse } = useGetBoardMessages(board.id);

  const onSaveMessage = () => {
    setFiles([]);
    localStorage.removeItem(boardMessageKey(board.id));
  };

  const { saveCustomerMessage, saving } = useCustomerMessage(onSaveMessage);

  const designatorOptions = billOfMaterials?.elements.map((element) => ({
    value: element.name,
    label: element.name,
  })) || [{ value: "", label: "" }];

  return (
    <QuoteToolContainer>
      <LoadingOverlay loading={saving} text={"Saving..."} />
      <QuoteToolTitle>
        Message a FlashPCB engineer about your board
      </QuoteToolTitle>
      <MessageList boardId={board.id} maxHeight={"20rem"} />
      <Formik
        initialValues={{
          designators:
            messagesResponse && messagesResponse.messages.length > 0
              ? messagesResponse.messages[0]!.designators
              : [],
          message: localStorage.getItem(boardMessageKey(board.id)) || "",
          internalOnly:
            localStorage.getItem(boardMessageInternalKey(board.id)) == "true",
        }}
        onSubmit={(values: CustomerMessageFormValues, actions) => {
          saveCustomerMessage(board.id, values, files);
          actions.resetForm();
        }}
        validationSchema={customerMessageSchema}
        enableReinitialize={true}
      >
        {({ handleSubmit, isValid, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <StoreMessage
                message={values.message}
                boardId={board.id}
                internalOnly={values.internalOnly}
              />
              <MessagesFieldset disabled={boardStuff.isDemoBoard}>
                <FormikSelect
                  name={"designators"}
                  label={"Select relevant designators (optional)"}
                  options={designatorOptions}
                  isClearable={true}
                  isMulti={true}
                />
                <FormikFormLine
                  name={"message"}
                  placeholder={"I would like to substitute this part..."}
                  label={
                    isAdminOrEngineer
                      ? "Message customer"
                      : "Message a FlashPCB engineer"
                  }
                  isTextArea={true}
                />
                {isAdminOrEngineer && (
                  <FormikCheckbox
                    name={"internalOnly"}
                    label={
                      "This message should only be visible to FlashPCB engineers"
                    }
                  />
                )}
                <Button
                  type={ButtonTypes.SUBMIT}
                  label={"Submit"}
                  disabled={!isValid || saving}
                />
                <FakeLink
                  disabled={saving}
                  onClick={() => !saving && setUploadFiles(true)}
                >
                  Attach files{" "}
                  {files.length > 0 && `(${files.length} attached)`}
                </FakeLink>
              </MessagesFieldset>
            </form>
          );
        }}
      </Formik>
      {uploadFiles && (
        <Modal
          isOpen={uploadFiles}
          onRequestClose={() => setUploadFiles(false)}
        >
          <ModalContentWithCloseButton closeModal={() => setUploadFiles(false)}>
            <FileUploadContainer>
              <FileInput files={files} setFiles={setFiles} isMultiple={true} />
              <Button
                onClick={() => setUploadFiles(false)}
                label={"Continue"}
              />
            </FileUploadContainer>
          </ModalContentWithCloseButton>
        </Modal>
      )}
    </QuoteToolContainer>
  );
};

export default CustomerMessages;
